// ============================================================================
// QUERY UTILITIES
// ============================================================================

export interface SafeQueryOptions {
  operation?: string;
  fallbackMessage?: string;
}

export interface SafeQueryResult<T> {
  data: T | null;
  error: { message: string } | null;
}

/**
 * Wraps a Supabase query promise with error normalization.
 * Returns a consistent { data, error } shape regardless of how the query fails.
 */
export async function safeQuery<T>(
  queryPromise: PromiseLike<{ data: T | null; error: unknown }>,
  options: SafeQueryOptions = {}
): Promise<SafeQueryResult<T>> {
  const { fallbackMessage = 'An unexpected error occurred.' } = options;
  try {
    const { data, error } = await queryPromise;
    if (error) {
      const message =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message: unknown }).message)
          : fallbackMessage;
      return { data: null, error: { message } };
    }
    return { data, error: null };
  } catch (err) {
    // Re-throw abort errors so callers can handle them specifically
    if (
      err instanceof DOMException && err.name === 'AbortError'
    ) {
      throw err;
    }
    if (err && typeof err === 'object' && 'code' in err && (err as any).code === '20') {
      throw err;
    }
    const message =
      err instanceof Error ? err.message : fallbackMessage;
    return { data: null, error: { message } };
  }
}

// ============================================================================
// LOGGING UTILITIES
// ============================================================================

/**
 * Log successful query for debugging (dev only)
 */
export function logQuerySuccess(operation: string, data: unknown): void {
  if (import.meta.env.DEV) {
    // Use browser DevTools or a proper logging service instead
  }
}

/**
 * Log query timing for performance monitoring (dev only)
 */
export function logQueryTiming(operation: string, startTime: number): void {
  if (import.meta.env.DEV) {
    const duration = Date.now() - startTime;
    // Use browser DevTools Performance tab or a proper monitoring service instead
  }
}
